import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Compass, Heart, Globe, Users, GraduationCap, Target,
  Lightbulb, Shield, TrendingUp, Award
} from 'lucide-react';

const Mission = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-6">
            <Compass className="h-16 w-16 text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Our Mission
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto leading-relaxed">
            Empowering every international student to find their way forward to higher education, 
            regardless of background or circumstances.
          </p>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8 md:p-12">
              <div className="flex items-center gap-3 mb-6">
                <Target className="h-8 w-8 text-emerald-600" />
                <h2 className="text-2xl font-bold">What We Stand For</h2>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                At Wayo, we believe that access to higher education should be a right, not a privilege. 
                Our platform is designed to simplify the college application process, making it more 
                accessible, equitable, and efficient for students internationally.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                We are committed to breaking down barriers that prevent talented students from 
                reaching their full potential. Through adaptive AI technology and compassionate 
                support, we guide students on their unique paths to success.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-16 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              These principles guide everything we do at Wayo
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="bg-emerald-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <Heart className="h-7 w-7 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Accessibility</h3>
                <p className="text-gray-600">
                  We believe every student deserves equal access to educational opportunities, 
                  regardless of their socioeconomic status or geographic location.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="bg-teal-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <Shield className="h-7 w-7 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Integrity</h3>
                <p className="text-gray-600">
                  We operate with honesty and transparency, protecting student data 
                  and maintaining the highest ethical standards in all we do.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="bg-cyan-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <Lightbulb className="h-7 w-7 text-cyan-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Innovation</h3>
                <p className="text-gray-600">
                  We continuously improve our platform with cutting-edge technology 
                  to better serve students and institutions.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="bg-emerald-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <Users className="h-7 w-7 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Community</h3>
                <p className="text-gray-600">
                  We foster connections between students, counselors, and colleges 
                  to create a supportive ecosystem for success.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="bg-teal-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <Globe className="h-7 w-7 text-teal-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Diversity</h3>
                <p className="text-gray-600">
                  We celebrate and support students from all backgrounds, 
                  recognizing that diversity enriches higher education.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="bg-cyan-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="h-7 w-7 text-cyan-600" />
                </div>
                <h3 className="text-xl font-semibold mb-3">Excellence</h3>
                <p className="text-gray-600">
                  We strive for excellence in everything we do, empowering 
                  students to achieve their highest potential.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Impact */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Impact</h2>
            <p className="text-gray-600">We intend to make a difference in students' lives</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-emerald-600 mb-2">1M+</div>
              <p className="text-gray-600">We intend to serve students annually</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-teal-600 mb-2">900+</div>
              <p className="text-gray-600">We intend to partner with colleges</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-cyan-600 mb-2">50+</div>
              <p className="text-gray-600">We intend to represent countries</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-emerald-600 mb-2">$2B+</div>
              <p className="text-gray-600">We intend to facilitate in scholarships</p>
            </div>
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Award className="h-12 w-12 text-emerald-600 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Our Commitment</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            We are dedicated educators, technologists, and advocates united by a shared belief 
            in the transformative power of education. Every day, we work to ensure that the 
            path to higher education is clear, accessible, and achievable for every student 
            who dreams of a brighter future.
          </p>
          <Link to="/signup">
            <Button size="lg" className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600">
              <GraduationCap className="h-5 w-5 mr-2" />
              Start Your Journey Today
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Mission;
